import Head from 'next/head';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { formatStringLowercaseWithDashes } from 'shared/utils/formatStringLowercaseWithDashes';
import { getBreadcrumbSchema } from 'productTemplate/utils/getBreadcrumbSchema';
import { getCollectionPageSchema } from 'productTemplate/utils/getCollectionPageSchema';
import { IBreadcrumb } from 'shared/types/Breadcrumbs';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { SearchGrid } from 'search/components/searchGrid/SearchGrid';
import { startCase } from 'shared/utils/startCase';
import { useCurrency } from 'shared/hooks/useCurrency';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { t } from 'localization/utils/t';

const TP = 'productLandingPages.components.PlpPageComponent';

export interface IPlpPageComponent {
  brandName?: string;
  breadcrumbs?: IBreadcrumb[];
  browseKey: string;
  browseValue: string;
  constructorResponse: IConstructorBrowseResponse;
  category?: string;
  description?: string;
  displayName: string;
  globalPricingId: string;
  hasBreadcrumbSchema?: boolean;
  hasLink?: boolean;
  linkDestination?: string;
  linkTitle?: string;
  metadataDescription?: string;
  metaImage?: string;
  metaTitle?: string;
  silhouette?: string;
  isCrawler: boolean;
}

export const PlpPageComponent = ({
  brandName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  description = null,
  displayName,
  globalPricingId,
  hasBreadcrumbSchema = false,
  hasLink,
  linkDestination,
  linkTitle,
  metadataDescription,
  metaImage,
  metaTitle,
  silhouette,
  isCrawler,
}: IPlpPageComponent) => {
  const { locale, query, asPath } = useRouter();
  const { pageTitle, pageDescription } = usePageMetadata({
    constructorResponse,
    mainTitle: metaTitle,
    mainDescription: metadataDescription,
    defaultTitle: t(`${TP}.metaTitle`, 'Buy {displayName} ', {
      displayName,
    }),
  });
  const { selectedCurrencyCode } = useCurrency();

  const fashionSchema = [
    {
      id: `/brand/${formatStringLowercaseWithDashes(brandName)}`,
      name: startCase(brandName),
    },
    {
      id: `/apparel/brand/${formatStringLowercaseWithDashes(brandName)}`,
      name: brandName
        ? t(`${TP}.brandNameApparel`, '{brand} Apparel', { brand: String(startCase(brandName)) })
        : null,
    },
  ];
  const sneakerSchema = [
    {
      id: `/brand/${formatStringLowercaseWithDashes(brandName)}`,
      name: startCase(brandName),
    },
    {
      id: `/sneakers/brand/${formatStringLowercaseWithDashes(brandName)}`,
      name: brandName
        ? t(`${TP}.brandNameSneakers`, '{brand} Sneakers', { brand: String(startCase(brandName)) })
        : null,
    },
    {
      id: `/sneakers/silhouette/${formatStringLowercaseWithDashes(silhouette)}`,
      name: startCase(silhouette),
    },
  ];

  return (
    <PlpPageComponent.Container>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" key="og:title" content={pageTitle} />
        <meta name="twitter:title" content={pageTitle} key="twitter:title" />
        <meta
          name="description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="description"
        />
        <meta
          property="og:description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="og:description"
        />
        <meta
          name="twitter:description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="twitter:description"
        />
        {metaImage && (
          <>
            <meta name="twitter:image" content={metaImage} key="twitter:image" />
            <meta property="og:image" content={metaImage} key="og:image" />
          </>
        )}
        {hasBreadcrumbSchema && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: getBreadcrumbSchema(
                category === 'sneakers' ? sneakerSchema : fashionSchema,
                locale,
              ),
            }}
          />
        )}
        {query?.silhouette && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: getCollectionPageSchema({
                constructorResponse,
                locale,
                asPath,
                description: metadataDescription,
                silhouette: startCase(silhouette),
                query,
                globalPricingId,
                selectedCurrencyCode,
              }),
            }}
          />
        )}
      </Head>
      <SearchGrid
        breadcrumbs={breadcrumbs}
        browseKey={browseKey}
        browseValue={browseValue}
        constructorResponse={constructorResponse}
        description={description}
        globalPricingId={globalPricingId}
        hasLink={hasLink}
        linkDestination={linkDestination}
        linkTitle={linkTitle}
        title={displayName}
        isCrawler={isCrawler}
      />
    </PlpPageComponent.Container>
  );
};

PlpPageComponent.Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  position: relative;
`;
